import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import { CopyButton } from '../../ui/copy.js';
import {
  claudeCodeCommand,
  curlInitialize,
  geminiCliConfig,
  mcpClientConfig,
  mcpJsonConfig,
  tenantUrls,
  vsCodeConfig,
} from '../../ui/endpoints.js';

/**
 * The handoff: how a person actually adds THIS tenant to the agent they use —
 * whichever agent that is.
 *
 * This file used to be a screen of its own, rendering one card per tenant from
 * the same list the Sites table renders — the Sites table with the URLs
 * expanded, and a second place to edit the day a client's config shape changes.
 * The card is now the tenant's own Connect tab, and what is left in this file
 * is the card, its parts, and the protocol facts.
 *
 * The first version of this screen addressed claude.ai and nobody else, which
 * was honest about where the widgets had been tested and wrong about who the
 * customer is. A tenant is a remote MCP server; every client that speaks
 * Streamable HTTP can add it with the same URL, and since the `.openai` twin
 * resources exist, ChatGPT renders the same widget claude.ai does. So the
 * screen is organised by platform, each section carrying the tenant's real
 * URLs and the exact shape that platform's config takes, with a copy button
 * on every string a person would otherwise retype.
 *
 * The order is deliberate. The business owner's path (a connector form that
 * takes a URL and nothing else) leads, on the two chat products a customer is
 * most likely to be sitting in; the editor and CLI configs sit under those for
 * the developer; "any MCP client" states the protocol facts for someone
 * integrating from scratch; and the website embed closes, because it is the
 * one section that is not about a chat client at all but about the tenant's
 * own page.
 *
 * The screen states that the endpoint is keyless and read-only, because that is
 * the fact that makes "Authentication: None" a reasonable thing to ask a person
 * to click. Nothing published here can write, book, pay or authenticate; the
 * strongest capability kind is `stage`, which returns a deep link back to the
 * origin for a human to confirm. A connector screen that asked for that click
 * without saying so would be training people to accept it anywhere.
 *
 * Protocol facts quoted below are copied from `apps/server/src/mcp/jsonrpc.ts`
 * (`SUPPORTED_PROTOCOL_VERSIONS`); the console cannot import the server, so
 * the list is restated here with that pointer, and changes there must be
 * mirrored here.
 */

/** Mirror of SUPPORTED_PROTOCOL_VERSIONS in apps/server/src/mcp/jsonrpc.ts, newest first. */
export const SUPPORTED_PROTOCOL_VERSIONS = ['2026-07-28', '2025-06-18', '2025-03-26'] as const;

export interface CopyCtx {
  copied: string | null;
  onCopy: (id: string, text: string) => void;
}

/** A named block of text with one copy button: a config file, a command, a tag. */
export function Snippet(props: {
  id: string;
  name: string;
  text: string;
  describe: string;
  label?: string;
  ctx: CopyCtx;
}): VNode {
  return html`
    <div class="snippet" style="margin-top:10px">
      <div class="snippet__bar">
        <span class="snippet__name">${props.name}</span>
        ${CopyButton({
          id: props.id,
          text: props.text,
          copied: props.ctx.copied,
          onCopy: props.ctx.onCopy,
          label: props.label,
          describe: props.describe,
        })}
      </div>
      <pre class="snippet__code"><code>${props.text}</code></pre>
    </div>
  `;
}

/** One URL row with a copy button; the same row the tenant screen uses. */
function UrlRow(props: { tag: string; value: string; id: string; describe: string; ctx: CopyCtx }): VNode {
  return html`
    <div class="url">
      <span class="url__tag">${props.tag}</span>
      <a class="url__value" href=${props.value} target="_blank" rel="noopener noreferrer">
        ${props.value}
      </a>
      ${CopyButton({
        id: props.id,
        text: props.value,
        copied: props.ctx.copied,
        onCopy: props.ctx.onCopy,
        describe: props.describe,
      })}
    </div>
  `;
}

/**
 * A platform section: a native `<details>`, so it is keyboard-operable with no
 * script (Space/Enter on the summary), announced as expandable, and remembers
 * nothing — every reload shows the same first section open.
 */
export function Platform(props: { title: string; open?: boolean; children?: unknown }): VNode {
  return html`
    <details class="devs" open=${props.open ? true : undefined} style="margin-top:10px">
      <summary class="devs__summary" style="font-family:inherit;font-size:14.5px;font-weight:600;letter-spacing:0">
        ${props.title}
      </summary>
      <div style="padding:2px 0 14px">${props.children}</div>
    </details>
  `;
}

export function ConnectCard(props: {
  tenantId: string;
  displayName: string;
  tools: number;
  origin: string;
  ctx: CopyCtx;
}): VNode {
  const { tenantId, displayName, tools, origin, ctx } = props;
  const u = tenantUrls(origin, tenantId);
  const cli = claudeCodeCommand(tenantId, u.mcp);
  const claudeJson = mcpClientConfig(tenantId, u.mcp);
  const mcpJson = mcpJsonConfig(tenantId, u.mcp);
  const vscodeJson = vsCodeConfig(tenantId, u.mcp);
  const geminiJson = geminiCliConfig(tenantId, u.mcp);
  const curl = curlInitialize(u.mcp, SUPPORTED_PROTOCOL_VERSIONS[1]);

  return html`
    <li class="concard">
      <div class="concard__top">
        <h3 class="concard__name">${displayName}</h3>
        <code class="concard__id">${tenantId}</code>
        <span class="concard__tools">${tools} ${tools === 1 ? 'tool' : 'tools'}</span>
      </div>

      ${UrlRow({ tag: 'MCP', value: u.mcp, id: `mcp-${tenantId}`, describe: `the MCP endpoint for ${tenantId}`, ctx })}
      ${UrlRow({ tag: 'Page', value: u.page, id: `page-${tenantId}`, describe: `the branded page for ${tenantId}`, ctx })}

      ${Platform({
        title: 'Claude — claude.ai, Claude Desktop, Claude Code',
        open: true,
        children: html`
          <p class="embed__p">
            In <b>claude.ai</b> or <b>Claude Desktop</b>: <b>Settings → Connectors → Add custom
            connector</b>, paste the MCP URL above, leave <b>Authentication</b> on <b>None</b>, save.
            That is the whole configuration; the widgets render inside the chat as MCP Apps.
          </p>
          <p class="embed__p">In <b>Claude Code</b>, one command:</p>
          ${Snippet({ id: `cli-${tenantId}`, name: 'terminal', text: cli, label: 'Copy CLI', describe: `the Claude Code command for ${tenantId}`, ctx })}
          <p class="embed__p" style="margin-top:12px">
            Or the same server as JSON, for a project's <code>.mcp.json</code> or${SP}
            <code>claude_desktop_config.json</code> where a host reads a file rather than a form:
          </p>
          ${Snippet({ id: `config-${tenantId}`, name: '.mcp.json · claude_desktop_config.json', text: claudeJson, describe: `the Claude JSON config for ${tenantId}`, ctx })}
        `,
      })}

      ${Platform({
        title: 'ChatGPT — OpenAI Apps SDK',
        children: html`
          <p class="embed__p">
            <b>Settings → Connectors → Create</b>, paste the MCP URL above, no authentication. The
            Create button needs <b>Developer mode</b> enabled under Settings → Connectors →
            Advanced; whether that is available depends on the ChatGPT plan and the workspace
            policy, so if the button is missing, that is the reason rather than the URL.
          </p>
          <p class="embed__p">
            Each tool carries <code>_meta["openai/outputTemplate"]</code>, naming a${SP}
            <code>text/html+skybridge</code> twin of the MCP Apps widget — the same bytes under a
            second resource URI — so ChatGPT renders the same branded panel claude.ai does, reading
            the result from <code>window.openai.toolOutput</code>. The tools stay read-only there
            too: the widget cannot call tools back, and the only link out is the deep link to the
            business's own site.
          </p>
        `,
      })}

      ${Platform({
        title: 'Cursor, VS Code, Windsurf, Gemini CLI',
        children: html`
          <p class="embed__p">
            Editors and CLIs read a JSON file. The shape differs by a key or two; the server is the
            same one, keyless, so no <code>headers</code> or <code>env</code> block is ever needed.
          </p>
          ${Snippet({ id: `mcpjson-${tenantId}`, name: 'Cursor .cursor/mcp.json · Windsurf mcp_config.json', text: mcpJson, describe: `the mcp.json config for ${tenantId}`, ctx })}
          ${Snippet({ id: `vscode-${tenantId}`, name: 'VS Code .vscode/mcp.json', text: vscodeJson, describe: `the VS Code config for ${tenantId}`, ctx })}
          ${Snippet({ id: `gemini-${tenantId}`, name: 'Gemini CLI ~/.gemini/settings.json', text: geminiJson, describe: `the Gemini CLI config for ${tenantId}`, ctx })}
          <p class="embed__foot">
            Gemini CLI takes <code>httpUrl</code> for Streamable HTTP; its <code>url</code> key means
            the older SSE transport, which this server does not serve.
          </p>
        `,
      })}

      ${Platform({
        title: 'Any MCP client',
        children: html`
          <ul class="embed__p" style="padding-left:18px">
            <li><b>Transport:</b> Streamable HTTP, <code>POST</code> JSON-RPC to the MCP URL. Stateless under the current revision — no session id to keep, no <code>GET</code> stream to hold open.</li>
            <li><b>Auth:</b> none. No key, no OAuth, no header. The endpoint is keyless by design (see below).</li>
            <li><b>Access:</b> read-only. Every tool is <code>readOnlyHint</code>; nothing writes.</li>
            <li>
              <b>Protocol revisions:</b>${SP}
              ${SUPPORTED_PROTOCOL_VERSIONS.map((v, i) => html`${i ? ', ' : ''}<code>${v}</code>`)}.
              A client that offers another revision is negotiated to the nearest one it can accept.
            </li>
            <li>
              <b>OAuth protected-resource metadata</b> (RFC 9728) would live at${SP}
              <code>${u.protectedResourceMetadata}</code>. Nothing is served there while the
              endpoint is keyless; a client that probes it gets a 404 and proceeds without auth.
            </li>
          </ul>
          <p class="embed__p">Check it the way a client does:</p>
          ${Snippet({ id: `curl-${tenantId}`, name: 'initialize', text: curl, label: 'Copy curl', describe: `the curl handshake for ${tenantId}`, ctx })}
        `,
      })}

    </li>
  `;
}

/**
 * Why the connector form should be left on "Authentication: None".
 *
 * It sits with the card rather than on a help page because it is read at the
 * moment a person is being asked to click past an auth prompt, and somebody who
 * accepts that prompt without knowing why is being trained to accept it
 * anywhere. The claim is checkable: the strongest capability kind this product
 * publishes is `stage`, which hands back a deep link for a human to confirm.
 */
export function WhyKeyless(): VNode {
  return html`
    <section class="handoff" aria-labelledby="handoff-h">
      <h2 class="handoff__h" id="handoff-h">Why "Authentication: None" is the right answer</h2>
      <p class="handoff__why">
        The endpoint is keyless by design: it serves one business's own public information,
        read-only, to whoever asks. No tool published by ClawHarbor can write, book, pay or
        authenticate — the strongest kind is <code>stage</code>, which hands back a deep link to
        the business's own site for a person to complete the transaction there — so there is no
        credential to protect and nothing a connector could spend. If a connector form ever asks
        you for a key on one of these URLs, something else is answering it.
      </p>
    </section>
  `;
}
